import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { euro } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";
import { RicercaFornitore } from "@/components/RicercaAsync";
import { RicercaServizio } from "@/components/RicercaServizio";
import { confermaSeDuplicato } from "@/lib/duplicati";

export const UNITA_MISURA = ["pz", "kg", "t", "m", "m²", "m³", "l", "h", "conf", "barra", "lastra", "rotolo"];
const NESSUNA = "__nessuna__";
export const voceVuota = () => ({ servizio_id: "", descrizione: "", quantita: "", unita: "", costo_unitario: "", costo_totale: "" });

const totaleVoce = (v) => {
  const q = Number(v.quantita), cu = Number(v.costo_unitario);
  if (v.quantita !== "" && v.costo_unitario !== "" && !Number.isNaN(q) && !Number.isNaN(cu)) return Math.round(q * cu * 100) / 100;
  return v.costo_totale === "" ? null : Number(v.costo_totale) || 0;
};
const autoVoce = (v) => v.quantita !== "" && v.costo_unitario !== "" && !Number.isNaN(Number(v.quantita)) && !Number.isNaN(Number(v.costo_unitario));

// form = { fornitore_id, numero_fattura, data, voci: [...] }; onSubmit(payloads[])
export function AcquistiForm({ form, setForm, onSubmit, saving }) {
  const { data: unitaServizi = [] } = useQuery({ queryKey: ["servizi-unita"], queryFn: async () => (await api.get("/servizi/unita")).data });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const voci = form.voci?.length ? form.voci : [voceVuota()];
  const setVoce = (i, patch) => setForm((f) => ({ ...f, voci: voci.map((v, j) => (j === i ? { ...v, ...(typeof patch === "function" ? patch(v) : patch) } : v)) }));
  const aggiungiVoce = () => setForm((f) => ({ ...f, voci: [...voci, voceVuota()] }));
  const rimuoviVoce = (i) => setForm((f) => ({ ...f, voci: voci.length > 1 ? voci.filter((_, j) => j !== i) : [voceVuota()] }));

  const dalServizio = (i, sv) => setVoce(i, (v) => (sv
    ? { servizio_id: sv.id, descrizione: sv.descrizione, unita: sv.unita || v.unita, costo_unitario: sv.prezzo_riferimento ? String(sv.prezzo_riferimento) : v.costo_unitario }
    : { servizio_id: "", descrizione: "" }));
  const unitaOpzioni = Array.from(new Set([...unitaServizi, ...UNITA_MISURA, ...voci.map((v) => v.unita).filter(Boolean)]));

  const vociCompilate = voci.filter((v) => v.descrizione.trim() || v.quantita !== "" || v.costo_unitario !== "" || v.costo_totale !== "");
  const totaleFattura = vociCompilate.reduce((s, v) => s + (totaleVoce(v) || 0), 0);

  const submit = async () => {
    if (!form.fornitore_id) return toast.error("Seleziona il fornitore");
    if (vociCompilate.length === 0) return toast.error("Aggiungi almeno un servizio");
    const payloads = [];
    for (const [i, v] of vociCompilate.entries()) {
      if (!v.servizio_id) return toast.error(`Voce ${i + 1}: seleziona un servizio dal catalogo`);
      const q = Number(v.quantita), cu = Number(v.costo_unitario);
      const haQta = v.quantita !== "" && q > 0;
      if ((v.quantita !== "" && !(q >= 0)) || (v.costo_unitario !== "" && cu < 0)) return toast.error(`Voce ${i + 1}: quantità e costo unitario devono essere ≥ 0`);
      const tot = totaleVoce(v);
      if (tot == null) return toast.error(`Voce ${i + 1}: inserisci l'importo oppure quantità e costo unitario`);
      payloads.push({
        fornitore_id: form.fornitore_id,
        numero_fattura: form.numero_fattura,
        data: form.data || undefined,
        descrizione: v.descrizione.trim(),
        servizio_id: v.servizio_id,
        quantita: haQta ? q : null,
        unita: haQta ? v.unita || "" : "",
        costo_unitario: haQta && v.costo_unitario !== "" ? cu : null,
        costo_totale: tot,
      });
    }
    if (!(await confermaSeDuplicato({ numero: form.numero_fattura, fornitore_id: form.fornitore_id }))) return;
    onSubmit(payloads);
  };

  return (
    <div className="space-y-3" data-testid="acquisti-form">
      <div className="grid sm:grid-cols-6 gap-3 items-end">
        <div className="space-y-1.5 sm:col-span-3">
          <Label>Fornitore *</Label>
          <RicercaFornitore value={form.fornitore_id} onChange={(v) => set("fornitore_id", v)} testid="mat-fornitore-select" />
        </div>
        <div className="space-y-1.5 sm:col-span-2"><Label>N. fattura</Label><Input value={form.numero_fattura} onChange={(e) => set("numero_fattura", e.target.value)} data-testid="mat-numero-input" /></div>
        <div className="space-y-1.5"><Label>Data</Label><Input type="date" value={form.data} onChange={(e) => set("data", e.target.value)} data-testid="mat-data-input" /></div>
      </div>

      <div className="rounded-lg border border-slate-200 overflow-hidden" data-testid="mat-voci">
        <div className="hidden sm:grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          <div className="col-span-5">Servizio (dal catalogo) *</div><div className="col-span-1">Q.tà</div><div className="col-span-1">U.M.</div><div className="col-span-2">Costo unit. (€)</div><div className="col-span-2">Importo (€)</div><div></div>
        </div>
        {voci.map((v, i) => {
          const auto = autoVoce(v);
          return (
            <div key={i} className="grid sm:grid-cols-12 gap-2 px-3 py-2 border-t border-slate-100 items-center" data-testid={`mat-voce-${i}`}>
              <div className="sm:col-span-5 min-w-0">
                <RicercaServizio value={v.servizio_id} onChange={(sv) => dalServizio(i, sv)} testid={i === 0 ? "mat-descrizione-input" : `mat-voce-${i}-descrizione`} />
              </div>
              <Input className="sm:col-span-1" type="number" min="0" step="0.01" value={v.quantita} onChange={(e) => setVoce(i, { quantita: e.target.value })} placeholder="—" data-testid={i === 0 ? "mat-quantita-input" : `mat-voce-${i}-quantita`} />
              <Select value={v.unita || NESSUNA} onValueChange={(u) => setVoce(i, { unita: u === NESSUNA ? "" : u })}>
                <SelectTrigger className="sm:col-span-1" data-testid={i === 0 ? "mat-unita-select" : `mat-voce-${i}-unita`}><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value={NESSUNA}>—</SelectItem>{unitaOpzioni.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
              <Input className="sm:col-span-2" type="number" min="0" step="0.0001" value={v.costo_unitario} onChange={(e) => setVoce(i, { costo_unitario: e.target.value })} placeholder="—" data-testid={i === 0 ? "mat-costo-unitario-input" : `mat-voce-${i}-costo-unitario`} />
              <Input className={`sm:col-span-2 ${auto ? "bg-orange-50 font-mono" : ""}`} type="number" min="0" step="0.01" readOnly={auto} value={auto ? totaleVoce(v) : v.costo_totale} onChange={(e) => setVoce(i, { costo_totale: e.target.value })} placeholder="0" data-testid={i === 0 ? "mat-costo-input" : `mat-voce-${i}-importo`} />
              <button type="button" onClick={() => rimuoviVoce(i)} className="text-slate-300 hover:text-red-600 justify-self-end" title="Rimuovi voce" data-testid={`mat-voce-${i}-rimuovi`}><Trash2 className="h-4 w-4" /></button>
            </div>
          );
        })}
        <div className="flex items-center justify-between px-3 py-2 border-t border-slate-100 bg-slate-50/60">
          <Button type="button" size="sm" variant="outline" className="h-8" onClick={aggiungiVoce} data-testid="mat-aggiungi-voce"><Plus className="h-3.5 w-3.5 mr-1" /> Aggiungi voce</Button>
          <div className="text-sm text-slate-600" data-testid="mat-totale-fattura">
            {vociCompilate.length} voc{vociCompilate.length === 1 ? "e" : "i"} · Totale fattura: <b className="font-mono text-slate-900">{euro(totaleFattura)}</b>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Button onClick={submit} disabled={saving} data-testid="mat-add-button"><Save className="h-4 w-4 mr-1" /> {saving ? "Salvataggio…" : `Salva fattura${vociCompilate.length > 1 ? ` (${vociCompilate.length} voci)` : ""}`}</Button>
        <p className="text-xs text-slate-400">I servizi si gestiscono in Anagrafica → Servizi. Quantità e costo unitario sono facoltativi: basta il servizio e l'importo.</p>
      </div>
    </div>
  );
}
